/*
 * Urban Customz Customizer — cart guard (deferred, included on every page by layout/theme.liquid).
 *
 * Customized lines carry the hidden property `_uc_locked`. Their unit price was computed for an
 * exact quantity matrix, so Shopify's plain quantity stepper must not change them. This script
 * hides the stepper and the per-line remove control on those lines only and offers
 * "Edit quantities" (reopens the design session at Step 3) and "Remove design" (removes every
 * line of the plan).
 *
 * IDENTITY (Phase 7B — the price ladder). A customized line is NEVER identified by its Shopify
 * variant id. On the ladder a line sits on the variant that *is* its price, so two colours at the
 * same price legitimately share one variant id and would collapse into one entry. Identity comes
 * from, in order:
 *   1. the cart LINE INDEX the theme rendered (`data-index`, `CartItem-N`, `CartDrawer-Item-N`,
 *      `Quantity-N`, `Drawer-quantity-N`) matched against the same index in /cart.js, and then
 *      cross-checked against the row's variant id before it is trusted;
 *   2. the variant id, but only when exactly one customized line in the cart carries it;
 *   3. the visible `Colour` / `Size` properties, when the theme prints them in the row;
 *   4. nothing — the row is still locked, but no quantity is printed, because no number would be
 *      true for the row the theme rendered.
 * The map itself is keyed by `_uc_line` (the plan's own line identifier) and carries `_uc_plan`,
 * `_uc_group` and the Shopify line key; removal works from those, never from a DOM index.
 *
 * Cost on normal pages: zero network calls. /cart.js is read only when a rendered cart line is
 * present in the DOM, i.e. the cart drawer or the cart page has items. Every other line, product
 * and app (including Zakeke) is untouched.
 */
(function () {
  "use strict";
  if (window.__ucCartGuard) return;
  window.__ucCartGuard = true;

  var lines = null; // _uc_line (or Shopify line key) → entry
  var byIndex = null; // 1-based cart line index → entry
  var byVariant = null; // variant id → [entry, ...]; a ladder variant can carry several
  var pending = null;
  var API_ORIGIN = "https://customize.urbancustomz.com";
  var ROW_SELECTOR = ".cart-item, .cus-cart-item, [data-cart-item], tr[id^='CartItem'], [id^='CartDrawer-Item']";

  function style() {
    if (document.getElementById("uc-cart-guard-css")) return;
    var s = document.createElement("style");
    s.id = "uc-cart-guard-css";
    s.textContent =
      "[data-uc-locked] quantity-input,[data-uc-locked] .quantity,[data-uc-locked] cart-remove-button,[data-uc-locked] .cart-item__remove,[data-uc-locked] .cus-remove-button,[data-uc-locked] .cart-remove-button{display:none!important}" +
      ".uc-cart-lock{display:flex;flex-wrap:wrap;gap:8px 14px;align-items:center;margin:6px 0 2px;font-size:12px;line-height:1.4}" +
      ".uc-cart-lock__qty{font-weight:600}.uc-cart-lock a,.uc-cart-lock button{background:none;border:0;padding:0;font:inherit;color:inherit;text-decoration:underline;cursor:pointer}" +
      ".uc-cart-lock__note{flex-basis:100%;opacity:.7}" +
      // The raw preview URL is a 100-character string the theme prints as plain text. It has to stay
      // ON the line so it survives into checkout, the confirmation email and the Shopify order, where
      // nothing runs this script — but in the cart it is replaced by a button.
      ".uc-prev-hide{display:none!important}" +
      ".uc-cart-lock__prev{font-weight:600;text-decoration:underline}" +
      ".uc-prev{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.72);padding:16px}" +
      ".uc-prev__box{background:#fff;color:#111;border-radius:12px;max-width:min(920px,100%);max-height:92vh;overflow:auto;padding:18px 18px 22px;box-shadow:0 20px 60px rgba(0,0,0,.35)}" +
      ".uc-prev__top{display:flex;align-items:baseline;justify-content:space-between;gap:16px;margin-bottom:12px}" +
      ".uc-prev__title{font-size:16px;font-weight:700}.uc-prev__sub{font-size:12px;opacity:.65}" +
      ".uc-prev__close{background:none;border:0;font-size:26px;line-height:1;cursor:pointer;padding:0 4px}" +
      ".uc-prev__grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:14px}" +
      ".uc-prev__side{display:flex;flex-direction:column;gap:6px}" +
      ".uc-prev__side img{width:100%;height:auto;display:block;border-radius:8px;background:#f4f4f4;aspect-ratio:1/1;object-fit:contain}" +
      ".uc-prev__side span{font-size:12px;font-weight:600;text-align:center}" +
      ".uc-prev__msg{font-size:13px;opacity:.7;padding:20px 4px}";
    document.head.appendChild(s);
  }

  function hasCartLines() {
    return !!document.querySelector("[data-quantity-variant-id], " + ROW_SELECTOR);
  }

  /* --------------------------------------------------------------- the cart */

  function load() {
    if (pending) return pending;
    pending = fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var map = {};
        var idx = {};
        var variants = {};
        var any = false;
        var orphans = [];
        (cart.items || []).forEach(function (it, i) {
          var p = it.properties || {};
          var index = i + 1; // Shopify line index, the same one the theme renders
          // A customizer line in the cart WITHOUT its design properties (direct add, cart link, Buy
          // again, or a hand-crafted /cart/add.js): it cannot be produced, so take it out before
          // checkout. Ladder products are permanent and their handles are guessable, so they are
          // checked the same way as session products (Phase 7B).
          var handle = typeof it.handle === "string" ? it.handle : "";
          var ours = handle.indexOf("uc-session-") === 0 || handle.indexOf("uc-custom-") === 0;
          if (!p._uc_plan && (ours || p._uc_locked || p._uc_line || p._uc_sig)) {
            orphans.push(it.key);
            return;
          }
          if (!p._uc_locked) return;
          any = true;
          var entry = {
            id: p._uc_line || it.key,
            line: p._uc_line || null,
            plan: p._uc_plan,
            group: p._uc_group || null,
            session: p._uc_session,
            product: p._uc_product || null,
            sig: p._uc_sig || null,
            colour: p.Colour || p.Color || null,
            // Only our own preview page: a line property is whatever the cart says, and it becomes a link.
            preview: typeof p.Preview === "string" && p.Preview.indexOf(API_ORIGIN + "/p/") === 0 ? p.Preview : null,
            size: p.Size || null,
            key: it.key,
            index: index,
            quantity: it.quantity,
            variant: String(it.variant_id),
          };
          map[entry.id] = entry;
          idx[index] = entry;
          variants[entry.variant] = (variants[entry.variant] || []).concat([entry]);
        });
        lines = any ? map : {};
        byIndex = any ? idx : {};
        byVariant = any ? variants : {};
        if (orphans.length) removeOrphans(orphans);
        return lines;
      })
      .catch(function () { lines = {}; byIndex = {}; byVariant = {}; return lines; })
      .then(function (m) { pending = null; return m; });
    return pending;
  }

  /* ------------------------------------------------------- row to cart line */

  function trailingNumber(value) {
    var m = /(\d+)\s*$/.exec(value || "");
    return m ? Number(m[1]) : null;
  }

  /** The line index the theme printed for this row, from whichever hook the theme happens to use. */
  function rowIndex(row) {
    var n = trailingNumber(row.getAttribute("id"));
    if (n) return n;
    var el = row.querySelector("[data-index]");
    if (el) {
      n = Number(el.getAttribute("data-index"));
      if (n > 0) return n;
    }
    el = row.querySelector("[id^='Quantity-'], [id^='Drawer-quantity-'], [id^='CartDrawer-Remove-'], [id^='Remove-'], [id^='Line-item-error-'], [id^='CartDrawer-LineItemError-']");
    if (el) {
      n = trailingNumber(el.getAttribute("id"));
      if (n) return n;
    }
    var link = row.querySelector("a[href*='line=']");
    if (link) {
      var m = /[?&]line=(\d+)/.exec(link.getAttribute("href") || "");
      if (m) return Number(m[1]);
    }
    return null;
  }

  function rowVariant(row) {
    var el = row.querySelector("[data-quantity-variant-id]");
    return el ? String(el.getAttribute("data-quantity-variant-id")) : null;
  }

  /**
   * Resolves one rendered row to one cart line. Returns { entry, exact }. `exact` is false when the
   * row could only be narrowed to a set of same-priced lines, in which case the row is still locked
   * but no quantity is shown.
   */
  function resolve(row) {
    var variant = rowVariant(row);
    var index = rowIndex(row);
    if (index && byIndex[index]) {
      var atIndex = byIndex[index];
      // The index is trusted only when the row's own variant agrees with it. A theme that rendered
      // its lines in a different order than /cart.js would fail this check and fall through.
      if (!variant || atIndex.variant === variant) return { entry: atIndex, exact: true };
    }
    if (!variant) return { entry: null, exact: false };
    var candidates = byVariant[variant] || [];
    if (!candidates.length) return { entry: null, exact: false };
    if (candidates.length === 1) return { entry: candidates[0], exact: true };
    // Same price, several lines: the theme may still print the visible properties in the row.
    var text = (row.textContent || "").replace(/\s+/g, " ");
    var matched = candidates.filter(function (c) {
      if (!c.colour && !c.size) return false;
      var colourOk = !c.colour || text.indexOf(c.colour) > -1;
      var sizeOk = !c.size || new RegExp("(^|[^A-Za-z])" + c.size + "([^A-Za-z]|$)").test(text);
      return colourOk && sizeOk;
    });
    if (matched.length === 1) return { entry: matched[0], exact: true };
    return { entry: candidates[0], exact: false };
  }

  /* -------------------------------------------------------------- the rows */

  function productUrl(entry) {
    if (!entry.product || !entry.session) return null;
    return "/products/" + entry.product + "?uc=" + encodeURIComponent(entry.session) + "&step=quantity";
  }

  /* ------------------------------------------------------------- preview */

  /**
   * The design, all printed sides, without leaving the cart.
   *
   * A cart line cannot carry a per-customer IMAGE — on the price ladder a variant is a price, shared
   * by everyone paying it — so the design travels as a link. A link is right for checkout, the
   * confirmation email and the Shopify order, where none of this script runs. In the cart it is a
   * hundred characters of URL printed as plain text, so here it becomes a button and a dialog.
   */
  function openPreview(url, colour) {
    var back = document.createElement("div");
    back.className = "uc-prev";
    back.setAttribute("role", "dialog");
    back.setAttribute("aria-modal", "true");
    back.setAttribute("aria-label", "Design preview");
    var box = document.createElement("div");
    box.className = "uc-prev__box";
    var top = document.createElement("div");
    top.className = "uc-prev__top";
    var heading = document.createElement("div");
    var title = document.createElement("div");
    title.className = "uc-prev__title";
    title.textContent = "Your design";
    heading.appendChild(title);
    if (colour) {
      var sub = document.createElement("div");
      sub.className = "uc-prev__sub";
      sub.textContent = colour;
      heading.appendChild(sub);
    }
    top.appendChild(heading);
    var close = document.createElement("button");
    close.type = "button";
    close.className = "uc-prev__close";
    close.setAttribute("aria-label", "Close preview");
    close.innerHTML = "&times;";
    top.appendChild(close);
    box.appendChild(top);
    var body = document.createElement("div");
    body.className = "uc-prev__msg";
    body.textContent = "Loading your design…";
    box.appendChild(body);
    back.appendChild(box);
    document.body.appendChild(back);

    var lastFocus = document.activeElement;
    function shut() {
      if (back.parentNode) back.parentNode.removeChild(back);
      document.removeEventListener("keydown", onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function onKey(e) { if (e.key === "Escape") shut(); }
    document.addEventListener("keydown", onKey);
    close.addEventListener("click", shut);
    back.addEventListener("click", function (e) { if (e.target === back) shut(); });
    close.focus();

    fetch(url + (url.indexOf("?") > -1 ? "&" : "?") + "format=json", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var sides = (data && data.sides) || [];
        if (!sides.length) { body.textContent = "This design has no preview yet."; return; }
        body.className = "uc-prev__grid";
        body.textContent = "";
        // The URLs come back relative to the API origin, which is cross-origin to the storefront.
        var base = url.replace(/\/p\/.*$/, "");
        sides.forEach(function (side) {
          var cell = document.createElement("div");
          cell.className = "uc-prev__side";
          var img = document.createElement("img");
          img.loading = "lazy";
          img.alt = side.label + " of your design";
          img.src = side.url.indexOf("http") === 0 ? side.url : base + side.url;
          var cap = document.createElement("span");
          cap.textContent = side.label;
          cell.appendChild(img);
          cell.appendChild(cap);
          body.appendChild(cell);
        });
      })
      .catch(function () {
        body.className = "uc-prev__msg";
        body.textContent = "";
        var a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noreferrer";
        a.textContent = "Open your design in a new tab";
        body.appendChild(a);
      });
  }

  /**
   * Hides the printed-out URL in a row. The theme decides how it prints properties, so this looks for
   * the text rather than for a selector, and only ever hides the smallest element that holds it.
   */
  function hidePreviewText(row, url) {
    var nodes = row.querySelectorAll("*");
    var best = null;
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.children.length) return;
      if ((el.textContent || "").indexOf(url) === -1) return;
      best = el;
    });
    var target = best;
    // Themes usually print "Preview: <url>" as one <dd>/<span>; walk up while the parent adds nothing
    // but the label, so the stray "Preview:" does not survive on its own.
    if (target && target.parentElement && target.parentElement !== row) {
      var parentText = (target.parentElement.textContent || "").replace(url, "").replace(/preview\s*:?/i, "").trim();
      if (!parentText) target = target.parentElement;
    }
    if (target && target !== row) target.classList.add("uc-prev-hide");
  }

  function apply() {
    if (!lines || !Object.keys(lines).length) return;
    var rows = document.querySelectorAll(ROW_SELECTOR);
    Array.prototype.forEach.call(rows, function (row) {
      if (row.hasAttribute("data-uc-locked")) return;
      var found = resolve(row);
      var entry = found.entry;
      if (!entry) return;
      row.setAttribute("data-uc-locked", "1");
      if (entry.line) row.setAttribute("data-uc-line", entry.line);
      if (entry.plan) row.setAttribute("data-uc-plan", entry.plan);
      var input = row.querySelector("[data-quantity-variant-id]");
      var host = (input && input.closest(".cart-item__quantity, .cus-cart-item__controls, td, .cart-item__details")) || row;
      var box = document.createElement("div");
      box.className = "uc-cart-lock";
      var qty = document.createElement("span");
      qty.className = "uc-cart-lock__qty";
      qty.textContent = found.exact ? "Qty " + entry.quantity : "Custom order";
      box.appendChild(qty);
      var href = productUrl(entry);
      if (href) {
        var edit = document.createElement("a");
        edit.href = href;
        edit.textContent = "Edit quantities";
        box.appendChild(edit);
      }
      if (entry.preview) {
        hidePreviewText(row, entry.preview);
        var prev = document.createElement("button");
        prev.type = "button";
        prev.className = "uc-cart-lock__prev";
        prev.textContent = "See design preview";
        prev.addEventListener("click", function () { openPreview(entry.preview, entry.colour); });
        box.appendChild(prev);
      }
      var remove = document.createElement("button");
      remove.type = "button";
      remove.textContent = "Remove design";
      remove.addEventListener("click", function () { removePlan(entry.plan); });
      box.appendChild(remove);
      var note = document.createElement("span");
      note.className = "uc-cart-lock__note";
      note.textContent = found.exact ? "Custom order · priced for these quantities" : "Quantities are set in the designer";
      box.appendChild(note);
      host.appendChild(box);
    });
  }

  /* --------------------------------------------------------------- removal */

  /**
   * Removes cart lines by their Shopify line key in ONE request. Line keys are stable
   * (`variant:md5(properties)`), so nothing shifts underneath us the way line indexes would, and one
   * request means the cart can never be left half-removed.
   */
  function removeLines(keys) {
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    return fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates }),
    });
  }

  function removeOrphans(keys) {
    var flag = "uc_orphans_removed";
    try { if (sessionStorage.getItem(flag) === keys.join(",")) return; sessionStorage.setItem(flag, keys.join(",")); } catch (e) {}
    removeLines(keys).then(function () {
      try { sessionStorage.setItem("uc_orphan_notice", "1"); } catch (e) {}
      location.reload();
    });
  }

  function removePlan(plan) {
    var keys = [];
    var session = null;
    var map = lines || {};
    Object.keys(map).forEach(function (id) {
      if (map[id].plan === plan) { keys.push(map[id].key); session = map[id].session; }
    });
    if (!keys.length) { location.reload(); return; }
    removeLines(keys).then(function () {
      try {
        var token = session ? localStorage.getItem("uc_session_token:" + session) : null;
        if (token) {
          fetch(API_ORIGIN + "/api/sessions/" + session + "/cart-release", { method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify({ planId: plan, reason: "removed" }), keepalive: true });
        }
      } catch (e) {}
      location.reload();
    });
  }

  function showOrphanNotice() {
    try { if (sessionStorage.getItem("uc_orphan_notice") !== "1") return; sessionStorage.removeItem("uc_orphan_notice"); } catch (e) { return; }
    var n = document.createElement("div");
    n.setAttribute("role", "status");
    n.style.cssText = "margin:12px auto;max-width:960px;padding:10px 14px;border:1px solid #c4151c;border-radius:6px;background:#fff;color:#17181a;font-size:14px";
    n.textContent = "A customized item without its design was removed from your cart. Reorder it from My Designs so your design is attached.";
    var main = document.querySelector("main") || document.body;
    main.insertBefore(n, main.firstChild);
  }

  /* ------------------------------------------------------------- lifecycle */

  function refresh() {
    if (!hasCartLines()) return; // nothing rendered to guard: no network call
    load().then(function (m) {
      if (Object.keys(m).length) { style(); apply(); }
    });
  }

  // Cart drawers and the cart page re-render their HTML through the Section Rendering API, so watch
  // for newly rendered cart lines and re-apply; /cart.js is re-read only when the set of rendered
  // lines actually changed. The signature is per LINE INDEX, not per variant, so two ladder lines
  // sharing one variant are two different signatures.
  var lastSig = null;
  function signature() {
    var ids = [];
    Array.prototype.forEach.call(document.querySelectorAll(ROW_SELECTOR), function (row) {
      var input = row.querySelector("[data-quantity-variant-id]");
      ids.push((rowIndex(row) || "?") + ":" + (input ? input.getAttribute("data-quantity-variant-id") : "?") + ":" + (input ? input.value || "" : ""));
    });
    return ids.sort().join(",");
  }
  var timer = null;
  var observer = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      if (muts[i].addedNodes && muts[i].addedNodes.length) {
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (!hasCartLines()) return;
          var sig = signature();
          if (sig === lastSig && lines) { apply(); return; }
          lastSig = sig;
          lines = null;
          refresh();
        }, 150);
        return;
      }
    }
  });
  function start() {
    showOrphanNotice();
    observer.observe(document.body, { childList: true, subtree: true });
    if (hasCartLines()) { lastSig = signature(); refresh(); }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
